import React, { useState, useMemo } from 'react';
import {
  Container, Typography, Box, Button, TextField, Autocomplete,
  IconButton, Tooltip, Card, CardContent, Paper,
} from '@mui/material';
import ResponsiveDataView from '../components/ResponsiveDataView';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ClearIcon from '@mui/icons-material/Clear';
import ResponsiveToolbar from '../components/ResponsiveToolbar';

const theme = createTheme({ direction: 'rtl', ...heIL });

const fetchClients = async (searchTerm = '') => {
  const params = { light: true };
  if (searchTerm) params.search = searchTerm;
  const { data } = await API.get('/clients/', { params });
  return data;
};

const QuotationsPage = () => {
  const navigate = useNavigate();
  const { userProfile } = useUser();

  const uiConfig = userProfile?.ui_config || {};
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {};
  const productLabel = generalSchema?.product?.label || 'מוצר';

  const orderConfig = userProfile?.group?.order_config || {};
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled && (uiConfig.allow_order_management || false);

  // Filters
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [clientFilter, setClientFilter] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortModel, setSortModel] = useState([{ field: 'QuotationDate', sort: 'desc' }]);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients', 'autocomplete', clientSearch],
    queryFn: () => fetchClients(clientSearch),
    staleTime: 5 * 60 * 1000,
  });

  const formatDateLocal = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const queryParams = useMemo(() => {
    const params = {
      page: paginationModel.page + 1,
      page_size: paginationModel.pageSize,
    };
    if (clientFilter) params.ClientBusinessID = clientFilter.ClientBusinessID;
    if (startDate) params.start_date = formatDateLocal(startDate);
    if (endDate) params.end_date = formatDateLocal(endDate);
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      params.ordering = sort === 'desc' ? `-${field}` : field;
    }
    return params;
  }, [paginationModel.page, paginationModel.pageSize, clientFilter, startDate, endDate, sortModel]);

  const { data: quotationsData, isLoading, refetch } = useQuery({
    queryKey: ['quotations', 'list', queryParams],
    queryFn: async () => {
      const { data } = await API.get('/quotations/list/', { params: queryParams });
      return data;
    },
    staleTime: 30 * 1000,
  });

  const rows = quotationsData?.results || [];
  const totalRows = quotationsData?.count || 0;

  const handleDelete = async (quotationId) => {
    if (!window.confirm('האם למחוק הצעת מחיר זו?')) return;
    try {
      await API.post('/quotation/delete/', { QuotationID: quotationId });
      refetch();
    } catch (err) {
      alert(err?.response?.data?.error || 'שגיאה במחיקה');
    }
  };

  const handlePrint = (quotationId) => {
    navigate(`/quotation/${quotationId}`);
  };

  const handleConvert = async (quotationId) => {
    // If storage is enabled, redirect to detail page for proper storage selection
    if (storageEnabled) {
      navigate(`/quotation/${quotationId}`, { state: { openConvert: true } });
      return;
    }
    if (!window.confirm('האם להמיר הצעת מחיר זו להזמנה?')) return;
    try {
      const { data } = await API.post('/quotation/convert/', { QuotationID: quotationId });
      navigate(`/order/${data.OrderID}`, { state: { from: '/orders' } });
    } catch (err) {
      alert(err?.response?.data?.error || 'שגיאה בהמרה');
    }
  };

  const clearFilters = () => {
    setClientFilter(null);
    setStartDate(null);
    setEndDate(null);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(prevModel => {
      if (newModel.pageSize !== prevModel.pageSize) {
        return { page: 0, pageSize: newModel.pageSize };
      }
      return newModel;
    });
  };

  const columns = [
    {
      field: 'QuotationNumber',
      headerName: 'מספר',
      flex: 0.6,
      minWidth: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <Typography
            sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline', fontWeight: 500 }}
            onClick={(e) => { e.stopPropagation(); navigate(`/quotation/${params.row.QuotationID}`); }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'ClientName',
      headerName: 'לקוח',
      flex: 1.2,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'QuotationDate',
      headerName: 'תאריך',
      flex: 0.8,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value) => {
        if (!value) return '';
        try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return value; }
      },
    },
    {
      field: 'ValidUntil',
      headerName: 'תוקף עד',
      flex: 0.8,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      mobile: false,
      valueFormatter: (value) => {
        if (!value) return '-';
        try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return value; }
      },
    },
    {
      field: 'TotalAmount',
      headerName: 'סה"כ',
      flex: 0.8,
      minWidth: 90,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value) => {
        return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(value || 0);
      },
    },
    {
      field: 'actions',
      headerName: 'פעולות',
      flex: 0.7,
      minWidth: 100,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <Tooltip title="הדפס">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handlePrint(params.row.QuotationID); }}>
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.Status !== 'converted' && (
            <Tooltip title="המר להזמנה">
              <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleConvert(params.row.QuotationID); }}>
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {params.row.Status !== 'converted' && (
            <Tooltip title="מחק">
              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(params.row.QuotationID); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const quotationCardRenderer = (row) => {
    const total = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(row.TotalAmount || 0);
    const dateStr = row.QuotationDate ? new Date(row.QuotationDate).toLocaleDateString('he-IL') : '';
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {row.ClientName || ''}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {total}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          #{row.QuotationNumber} {dateStr && `· ${dateStr}`}
        </Typography>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">הצעות מחיר</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()} sx={{ direction: 'ltr' }}>
                רענן
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/quotation/new')} sx={{ direction: 'ltr' }}>
                הצעת מחיר חדשה
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <ResponsiveToolbar>
            <Autocomplete
              size="small"
              sx={{ width: { xs: '100%', md: 280 }, minWidth: { xs: 0, md: 280 } }}
              options={clients}
              getOptionLabel={(option) => `${option.ClientName} (${option.ClientBusinessID})`}
              value={clientFilter}
              onChange={(_, newValue) => { setClientFilter(newValue); setPaginationModel(prev => ({ ...prev, page: 0 })); }}
              onInputChange={(_, value) => setClientSearch(value)}
              renderInput={(params) => <TextField {...params} label="סנן לפי לקוח" />}
              isOptionEqualToValue={(option, value) => option.ClientBusinessID === value?.ClientBusinessID}
              noOptionsText="לא נמצאו לקוחות"
            />
            <DatePicker
              label="מתאריך"
              value={startDate}
              onChange={(val) => { setStartDate(val); setPaginationModel(prev => ({ ...prev, page: 0 })); }}
              format="dd/MM/yyyy"
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 170 }, minWidth: { xs: 0, md: 170 }, '& .MuiInputBase-root': { direction: 'ltr' } } } }}
            />
            <DatePicker
              label="עד תאריך"
              value={endDate}
              onChange={(val) => { setEndDate(val); setPaginationModel(prev => ({ ...prev, page: 0 })); }}
              format="dd/MM/yyyy"
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 170 }, minWidth: { xs: 0, md: 170 }, '& .MuiInputBase-root': { direction: 'ltr' } } } }}
            />
            {(clientFilter || startDate || endDate) && (
              <Tooltip title="נקה סינון">
                <IconButton onClick={clearFilters}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </ResponsiveToolbar>

          {/* DataGrid */}
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Box sx={{
                width: '100%',
                '.MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                },
                '.MuiDataGrid-cell': {
                  fontSize: '0.95rem',
                },
              }}>
                <ResponsiveDataView
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row.QuotationID}
                  cardRenderer={quotationCardRenderer}
                  onRowClick={(row) => navigate(`/quotation/${row.QuotationID}`)}
                  emptyMessage="אין תוצאות"
                  loading={isLoading}
                  paginationMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationModelChange}
                  rowCount={totalRows}
                  pageSizeOptions={[25, 50, 100]}
                  pagination
                  sortingMode="server"
                  sortModel={sortModel}
                  onSortModelChange={(model) => setSortModel(model)}
                  disableRowSelectionOnClick
                  density="comfortable"
                  autoHeight
                  localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                  components={{
                    NoRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 5 }}>
                        <Typography color="text.secondary">אין הצעות מחיר להצגה</Typography>
                      </Box>
                    ),
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-row': { cursor: 'pointer' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                    '& .MuiDataGrid-footerContainer': { borderTop: '1px solid rgba(224, 224, 224, 1)' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Container>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default QuotationsPage;
